import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { stockOf } from "../lib/stock";

const CartContext = createContext(null);
const STORAGE_KEY = "dc_cart_v1";

const loadInitial = (key) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const capFor = (item) => (typeof item.stock === "number" ? item.stock : Infinity);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadInitial(STORAGE_KEY) || []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [coupon, setCoupon] = useState(() => loadInitial(STORAGE_KEY + "_coupon"));
  const [stockNotes, setStockNotes] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (coupon) localStorage.setItem(STORAGE_KEY + "_coupon", JSON.stringify(coupon));
    else localStorage.removeItem(STORAGE_KEY + "_coupon");
  }, [coupon]);

  const applyCoupon = (couponData) => setCoupon(couponData);
  const removeCoupon = () => setCoupon(null);

  const addItem = (product, qty = 1) => {
    const stock = stockOf(product);
    const inBasket = itemsRef.current.find((i) => i.product_id === product.id)?.quantity || 0;
    const added = Math.max(0, Math.min(qty, stock - inBasket));
    if (added === 0) {
      toast.info(stock === 0 ? `${product.name} is out of stock` : `Only ${stock} available, and they're all in your basket`);
      return 0;
    }
    if (added < qty) toast.info(`Only ${stock} available, so we added ${added}`);
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) => (i.product_id === product.id ? { ...i, quantity: i.quantity + added, stock } : i));
      }
      return [
        ...prev,
        { product_id: product.id, name: product.name, price: product.price, image_url: product.image_url, quantity: added, stock },
      ];
    });
    setDrawerOpen(true);
    return added;
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) return removeItem(productId);
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity: Math.min(qty, capFor(i)) } : i)));
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  };

  const clear = () => {
    setItems([]);
    setCoupon(null);
  };

  const refreshStock = useCallback(async () => {
    const current = itemsRef.current;
    if (!current.length) return [];
    try {
      const { data } = await api.post("/products/stock", { ids: current.map((i) => i.product_id) });
      const byId = Object.fromEntries(data.map((d) => [d.id, d.stock]));
      const notes = [];
      const next = [];
      current.forEach((i) => {
        const stock = byId[i.product_id] ?? 0;
        if (stock <= 0) {
          notes.push(`${i.name} just sold out, so we took it out of your basket.`);
        } else if (i.quantity > stock) {
          notes.push(`${i.name}: only ${stock} left, so we lowered your quantity to ${stock}.`);
          next.push({ ...i, quantity: stock, stock });
        } else {
          next.push({ ...i, stock });
        }
      });
      setItems(next);
      if (notes.length) setStockNotes(notes);
      return notes;
    } catch {
      return [];
    }
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        clear,
        subtotal,
        itemCount,
        drawerOpen,
        setDrawerOpen,
        coupon,
        applyCoupon,
        removeCoupon,
        refreshStock,
        stockNotes,
        clearStockNotes: () => setStockNotes([]),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
